import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  CHANNEL_NAMES,
  goalLine,
  liftOf,
  previewText,
  stepChannel,
  cadenceSteps,
  stepWhen,
} from './overviewModel';
import { C, cardStyle, Dot, FONT, Icon, PATHS, Plain, text14 } from './ui';

import styled from '@/styles';
import { JourneyStep, LifecycleJourney } from '@/services/lifecycleAPI';
import { trackLifecycleFeatureUsed } from '@/util/vexo';

export const GRID = '200px 260px 200px repeat(3, minmax(0, 1fr))';

const TAGS: Record<
  LifecycleJourney['definition']['group'],
  { label: string; border: string; color: string }
> = {
  profile: { label: 'Profile', border: C.stroke, color: C.black },
  compliance: {
    label: 'Compliance',
    border: 'rgb(185,179,236)',
    color: 'rgb(42,21,225)',
  },
  activation: { label: 'Activation', border: C.greenLine, color: C.green },
  retention: { label: 'Retention', border: 'rgb(240,210,130)', color: C.amber },
};

const StepIcon = styled('button', {
  appearance: 'none',
  border: 'none',
  padding: 0,
  position: 'relative',
  width: 24,
  height: 24,
  borderRadius: 100,
  background: C.greenSoft,
  boxShadow: `inset 0 0 0 1px ${C.greenLine}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover, &:focus-visible': { background: C.greenHover },
});

type TipState = {
  journey: LifecycleJourney;
  step: JourneyStep;
  left: number;
  top?: number;
  bottom?: number;
};

export type OpenStep = (journey: LifecycleJourney, step: JourneyStep) => void;

const TIP_WIDTH = 240;
const HIDE_MS = 150;

// The design's showTip: centred on the icon, kept on screen, above the icon
// when there is room (more than 240px above it), else below.
const placeTip = (el: HTMLElement) => {
  const r = el.getBoundingClientRect();
  const left = Math.max(
    8,
    Math.min(
      window.innerWidth - TIP_WIDTH - 8,
      r.left + r.width / 2 - TIP_WIDTH / 2,
    ),
  );
  return r.top > 240
    ? { left, bottom: window.innerHeight - r.top + 8 }
    : { left, top: r.bottom + 8 };
};

const StepTooltip = ({
  tip,
  onHold,
  onLeave,
  onOpen,
}: {
  tip: TipState;
  onHold: () => void;
  onLeave: () => void;
  onOpen: () => void;
}) =>
  createPortal(
    <div
      data-testid="stepTooltip"
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.left,
        top: tip.top ?? 'auto',
        bottom: tip.bottom ?? 'auto',
        width: TIP_WIDTH,
        minHeight: 142,
        background: C.white,
        borderRadius: 12,
        boxShadow: `inset 0 0 0 0.5px ${C.stroke}, 0 0 0 0.5px ${C.stroke}, 0px 2px 4px 0px rgba(0,0,0,0.04)`,
        padding: 16,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        zIndex: 400,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.4,
        color: C.black,
        textAlign: 'left',
      }}
      onMouseEnter={onHold}
      onMouseLeave={onLeave}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        <span>{stepWhen(tip.step)}</span>
        <Dot />
        <span>{CHANNEL_NAMES[stepChannel(tip.step)]}</span>
      </div>
      <span style={{ fontWeight: 600, alignSelf: 'stretch' }}>
        {tip.step.label}
      </span>
      {tip.step.preview && (
        <span style={{ alignSelf: 'stretch', color: C.grey }}>
          {previewText(tip.step.preview)}
        </span>
      )}
      <Plain
        style={{ fontWeight: 600, color: C.green, alignSelf: 'stretch' }}
        type="button"
        onClick={onOpen}
      >
        Click for more details
      </Plain>
    </div>,
    document.body,
  );

const JourneyRowView = ({
  journey,
  onTip,
  onLeave,
  onOpenStep,
  onOpenJourney,
}: {
  journey: LifecycleJourney;
  onTip: (
    journey: LifecycleJourney,
    step: JourneyStep,
    el: HTMLElement,
  ) => void;
  onLeave: () => void;
  onOpenStep: OpenStep;
  onOpenJourney: (key: string) => void;
}) => {
  const tag = TAGS[journey.definition.group] ?? TAGS.profile;
  const steps = cadenceSteps(journey.definition.steps);
  const scheduled = steps.filter((step) => step.kind !== 'goal_message').length;
  const stats = journey.stats?.['30d'];
  const lift = liftOf(stats);
  const key = journey.journey_key;

  return (
    <div
      data-testid={`journeyRow-${key}`}
      style={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 20,
        paddingBottom: 12,
        borderBottom: `1px solid ${C.stroke}`,
        ...text14,
        alignItems: 'start',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          alignItems: 'flex-start',
          padding: '4px 0',
        }}
      >
        <span
          data-testid={`journeyTag-${key}`}
          style={{
            padding: '2px 8px',
            borderRadius: 8,
            boxShadow: `inset 0 0 0 1px ${tag.border}`,
            color: tag.color,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          {tag.label}
        </span>
        <Plain
          data-testid={`journeyName-${key}`}
          style={{ fontWeight: 700, fontSize: 16 }}
          title="Open journey settings"
          type="button"
          onClick={() => onOpenJourney(key)}
        >
          {journey.name}
        </Plain>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '4px 0',
        }}
      >
        <span>{journey.definition.enters}</span>
        <span style={{ color: C.blue }}>Exit: {journey.definition.exits}</span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '4px 0',
        }}
      >
        <div
          data-testid={`journeyCadence-${key}`}
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <b style={{ fontWeight: 700 }}>
            {scheduled} {scheduled === 1 ? 'step' : 'steps'}
          </b>
          <Dot />
          <span>over {journey.window_days} days</span>
        </div>
        {steps.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 11,
              position: 'relative',
              alignSelf: 'flex-start',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 14,
                right: 14,
                top: 12,
                height: 1,
                background: C.greenLine,
              }}
            />
            {steps.map((step) => {
              const channel = stepChannel(step);
              return (
                <StepIcon
                  aria-label={`${stepWhen(step)} ${CHANNEL_NAMES[channel]}: ${step.label}`}
                  data-channel={channel}
                  data-testid={`stepIcon-${step.key}`}
                  key={step.key}
                  type="button"
                  onBlur={onLeave}
                  onClick={() => onOpenStep(journey, step)}
                  onFocus={(e) => onTip(journey, step, e.currentTarget)}
                  onMouseEnter={(e) => onTip(journey, step, e.currentTarget)}
                  onMouseLeave={onLeave}
                >
                  <Icon color={C.greenFill} d={PATHS[channel]} size={18} />
                </StepIcon>
              );
            })}
          </div>
        )}
      </div>
      <div
        data-testid={`journeyActive-${key}`}
        style={{ padding: '4px 0', fontWeight: 700 }}
      >
        {(stats?.active ?? 0).toLocaleString('en-US')}
      </div>
      <div data-testid={`journeyGoal-${key}`} style={{ padding: '4px 0' }}>
        {goalLine(stats)}
      </div>
      <div
        data-testid={`journeyLift-${key}`}
        style={{
          padding: '4px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 600,
          color: lift.up ? C.green : C.grey,
        }}
      >
        <span>{lift.text}</span>
        {lift.up && (
          <Icon color={C.green} d={PATHS.arrowUp} size={16} width={2} />
        )}
      </div>
    </div>
  );
};

const HEADINGS = [
  'Journey',
  'Enter/exit',
  'Cadence',
  'In journey',
  'Goal rate vs. Holdout',
  'Lift',
];

const JourneysCard = ({
  journeys,
  onOpenStep,
  onOpenJourney,
}: {
  journeys: LifecycleJourney[];
  onOpenStep: OpenStep;
  onOpenJourney: (key: string) => void;
}) => {
  const [tip, setTip] = useState<TipState | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const section = useRef<HTMLElement>(null);
  // One step_preview per step per visit: a hover is not a decision, and
  // sweeping the mouse over a row must not count as ten.
  const previewed = useRef(new Set<string>());

  const hold = () => clearTimeout(hideTimer.current);
  const hideSoon = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setTip(null), HIDE_MS);
  };

  useEffect(() => {
    if (!tip) return undefined;
    // Like the design, a scroll hides the tooltip rather than leaving it
    // pinned away from its icon.
    const hide = () => setTip(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    let target: EventTarget = window;
    let active = true;
    const ionContent = section.current?.closest('ion-content') as
      | (Element & { getScrollElement?: () => Promise<HTMLElement> })
      | null
      | undefined;
    window.addEventListener('scroll', hide, true);
    ionContent
      ?.getScrollElement?.()
      .then((scroller) => {
        if (!active) return;
        target = scroller;
        scroller.addEventListener('scroll', hide);
      })
      .catch(() => undefined);
    document.addEventListener('keydown', onKey);
    return () => {
      active = false;
      window.removeEventListener('scroll', hide, true);
      if (target !== window) target.removeEventListener('scroll', hide);
      document.removeEventListener('keydown', onKey);
    };
  }, [tip]);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const showTip = (
    journey: LifecycleJourney,
    step: JourneyStep,
    el: HTMLElement,
  ) => {
    hold();
    setTip({ journey, step, ...placeTip(el) });
    if (!previewed.current.has(step.key)) {
      previewed.current.add(step.key);
      trackLifecycleFeatureUsed({
        feature: 'step_preview',
        surface: 'overview',
        journey_key: journey.journey_key,
        step_key: step.key,
        channel: stepChannel(step),
      });
    }
  };

  const open: OpenStep = (journey, step) => {
    clearTimeout(hideTimer.current);
    setTip(null);
    onOpenStep(journey, step);
  };

  return (
    <section
      aria-label="Journeys"
      data-testid="journeysCard"
      ref={section}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <span style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.4 }}>
        Journeys
      </span>
      <div style={{ ...cardStyle, gap: 16, overflowX: 'auto' }}>
        <div
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: GRID,
            gap: 20,
            paddingBottom: 12,
            borderBottom: `1px solid ${C.stroke}`,
            fontWeight: 800,
            ...text14,
            color: C.grey,
          }}
        >
          {HEADINGS.map((heading) => (
            <span
              key={heading}
              role="columnheader"
              style={{ padding: '4px 0' }}
            >
              {heading}
            </span>
          ))}
        </div>
        {journeys.length === 0 && (
          <div
            style={{
              ...text14,
              color: C.grey,
              padding: '24px 0',
              textAlign: 'center',
            }}
          >
            Journeys appear here once they are set up.
          </div>
        )}
        {journeys.map((journey) => (
          <JourneyRowView
            journey={journey}
            key={journey.journey_key}
            onLeave={hideSoon}
            onOpenJourney={onOpenJourney}
            onOpenStep={open}
            onTip={showTip}
          />
        ))}
      </div>
      {tip && (
        <StepTooltip
          tip={tip}
          onHold={hold}
          onLeave={hideSoon}
          onOpen={() => open(tip.journey, tip.step)}
        />
      )}
    </section>
  );
};

export default JourneysCard;
